import { useState, useEffect, useCallback } from 'react';
import type { RollupResponse } from '../fixtures/analytics-fixture';
import { getDemoRollup, DEMO_TREND } from '../fixtures/analytics-fixture';
import type { TrendPoint } from '../fixtures/analytics-fixture';
import { apiFetch } from '../providers/api-rest-provider';
import { PARTICIPATION_COUNT_METRIC, participationCount, type ParticipationCount } from '../utils/participation-count';
import { boundedRequest } from '../utils/bounded-request';

export type RollupData = RollupResponse | ParticipationCount;

// Demo mode is an environment decision only; a URL parameter must never
// replace authenticated analytics with fixtures.
const isDemoMode = () => import.meta.env.VITE_DEMO_MODE === 'true';

const ORG_PATH_RE = /^\/(?:[a-z0-9_-]+(?:\/[a-z0-9_-]+)*)?$/;

export function canonicalOrgPath(value: unknown): string | null {
  return typeof value === 'string' && ORG_PATH_RE.test(value) ? value : null;
}

export function isOrgPathWithin(root: string, candidate: string): boolean {
  const canonicalRoot = canonicalOrgPath(root);
  const canonicalCandidate = canonicalOrgPath(candidate);
  if (!canonicalRoot || !canonicalCandidate) return false;
  if (canonicalRoot === '/') return true;
  return canonicalCandidate === canonicalRoot || canonicalCandidate.startsWith(`${canonicalRoot}/`);
}

export async function fetchRollup(
  orgPath: string,
  metric: string,
  period: string,
  compare?: 'children',
  signal?: AbortSignal,
): Promise<RollupData> {
  if (canonicalOrgPath(orgPath) !== orgPath) throw new Error('Invalid organization scope');
  if (metric === 'dq_count' || metric === 'disqualifications') {
    throw Error('The disqualification metric is retired; it is not an alias for participation declarations.');
  }
  const params = new URLSearchParams({ metric, orgPath, period });
  if (compare) params.set('compare', compare);
  const raw = await boundedRequest(
    (s) => apiFetch(`analytics/rollup?${params}`, { signal: s }),
    signal ?? new AbortController().signal,
    'Analytics request timed out. Reload explicitly.',
  );
  if (metric === PARTICIPATION_COUNT_METRIC) return participationCount(raw, orgPath, period, compare);
  const response = raw as RollupResponse;
  if (
    response.orgPath !== orgPath ||
    response.metric !== metric ||
    response.period !== period
  ) {
    throw new Error('Analytics response dimensions did not match request');
  }
  return response;
}

export function useRollup(orgPath:string,metric:'pass_rate',period:string,compare?:'children',sessionKey?:string|null):{data:RollupResponse|null;loading:boolean;error:string|null;reload:()=>void};
export function useRollup(orgPath:string,metric:string,period:string,compare?:'children',sessionKey?:string|null):{data:RollupData|null;loading:boolean;error:string|null;reload:()=>void};
export function useRollup(orgPath:string,metric:string,period:string,compare?:'children',sessionKey?:string|null):{data:RollupData|null;loading:boolean;error:string|null;reload:()=>void}{
  const token=sessionKey??null,key=JSON.stringify([orgPath,metric,period,compare,token]),[refresh,setRefresh]=useState(0);
  const [state,setState]=useState<{key:string;data:RollupData|null;loading:boolean;error:string|null}>({key:'',data:null,loading:true,error:null});
  useEffect(()=>{let current=true;const abort=new AbortController();setState({key,data:null,loading:true,error:null});
    const load=async()=>{try{
      const demo=isDemoMode();if(metric==='dq_count'||metric==='disqualifications')throw Error('The disqualification metric is retired.');
      if(demo&&metric===PARTICIPATION_COUNT_METRIC)throw Error('Governed participation counts are unavailable in demo mode.');
      if(!demo&&!navigator.onLine)throw Error('Current online authorization is required.');
      const demoData=demo?getDemoRollup(orgPath,metric):undefined;if(demo&&demoData?.orgPath!==orgPath)throw Error('Demo data is unavailable for the assigned organization scope');const data=demo?demoData!:await fetchRollup(orgPath,metric,period,compare,abort.signal);
      if(current&&navigator.onLine)setState({key,data,loading:false,error:null});
    }catch(e){if(current)setState({key,data:null,loading:false,error:e instanceof Error?e.message:'Count unavailable'});}};
    const offline=()=>{current=false;abort.abort();setState({key,data:null,loading:false,error:'Reconnect and explicitly reload the query.'});};window.addEventListener('offline',offline);void load();
    return()=>{current=false;abort.abort();window.removeEventListener('offline',offline);};
  },[key,refresh,orgPath,metric,period,compare,token]);
  const visible=state.key===key?state:{key,data:null,loading:true,error:null};
  return {...visible,reload:useCallback(()=>setRefresh(v=>v+1),[])};
}

export function useTrend(orgPath:string,metric:string,periods:string[],refresh=0,sessionKey?:string|null):{trend:TrendPoint[];loading:boolean;error:string|null}{
  const token=sessionKey??null,periodsKey=JSON.stringify(periods),key=JSON.stringify([orgPath,metric,periodsKey,token]);
  const [state,setState]=useState<{key:string;trend:TrendPoint[];loading:boolean;error:string|null}>({key:'',trend:[],loading:true,error:null});
  useEffect(()=>{let current=true;const abort=new AbortController();setState({key,trend:[],loading:true,error:null});
    const load=async()=>{try{
      const demo=isDemoMode();if(metric==='dq_count'||metric==='disqualifications')throw Error('The disqualification metric is retired.');
      if(demo&&metric===PARTICIPATION_COUNT_METRIC)throw Error('Governed participation counts are unavailable in demo mode.');
      if(!demo&&!navigator.onLine)throw Error('Current online authorization is required.');
      const trend=demo?DEMO_TREND:await Promise.all((JSON.parse(periodsKey) as string[]).map(async period=>{const r=await fetchRollup(orgPath,metric,period,undefined,abort.signal);return {period,label:period.slice(5),value:r.metric===PARTICIPATION_COUNT_METRIC?r.total.count:(r as RollupResponse).total.avg};}));
      if(current&&navigator.onLine)setState({key,trend,loading:false,error:null});
    }catch(e){if(current)setState({key,trend:[],loading:false,error:e instanceof Error?e.message:'Trend unavailable'});}};
    const offline=()=>{current=false;abort.abort();setState({key,trend:[],loading:false,error:'Trend unavailable while offline.'});};window.addEventListener('offline',offline);void load();
    return()=>{current=false;abort.abort();window.removeEventListener('offline',offline);};
  },[key,refresh,orgPath,metric,periodsKey,token]);
  return state.key===key?state:{trend:[],loading:true,error:null};
}

export function exportToCsv(rows: Record<string, unknown>[], filename: string) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => JSON.stringify(r[h] ?? '')).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
